import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Clock, Brain, Users, Zap, Shield, TrendingUp, PhoneCall, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import Header from '../components/marketing/Header';
import Footer from '../components/marketing/Footer';
import SubscriptionInterestForm from '../components/SubscriptionInterestForm';
import TalkToAlfred from '../components/TalkToAlfred';

const LandingPageEnhanced = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setIsFormOpen(true);
  };

  const technicalFeatures = [
    {
      icon: Clock,
      title: 'Intelligent Business Hours',
      description: 'Automatically adjusts responses based on your operating schedule, holidays, and special events. AI knows when to take messages vs. connect callers.'
    },
    {
      icon: Brain,
      title: 'Custom Knowledge Base',
      description: 'We import your FAQs, procedures, product catalog, and documentation. AI learns your business inside-out before the first call.'
    },
    {
      icon: Users,
      title: 'Smart Staff Routing',
      description: 'Configurable routing logic sends calls to the right department or person. Check availability, transfer seamlessly, or take detailed messages.'
    },
    {
      icon: Zap,
      title: '3-Pipeline Architecture',
      description: 'Triple-redundant system with OpenAI Realtime, AssemblyAI + Claude, and Emergency IVR. If one fails, others take over automatically.'
    },
    {
      icon: Shield,
      title: 'Voice Personality Tuning',
      description: 'We configure tone, pacing, formality, and brand voice to match your company culture. Professional, friendly, urgent—your choice.'
    },
    {
      icon: TrendingUp,
      title: 'Advanced Call Analytics',
      description: 'Every call transcribed, analyzed for sentiment, intent, and outcomes. Searchable history with automated email summaries.'
    }
  ];

  const inboundFeatures = [
    'Answer customer inquiries 24/7',
    'Check business hours and location info',
    'Route to sales, support, or billing',
    'Take messages with email notifications',
    'Transfer to available staff members',
    'Capture appointment requests',
    'Answer FAQs from knowledge base',
    'Handle multiple calls simultaneously'
  ];

  const outboundFeatures = [
    'Automated appointment reminders',
    'Customer follow-up campaigns',
    'Survey and feedback collection',
    'Payment reminders and confirmations',
    'Service renewal notifications',
    'Promotional campaigns with tracking',
    'Lead qualification and scoring',
    'Structured data collection'
  ];

  const whiteGloveProcess = [
    {
      step: '1',
      title: 'Discovery Call',
      description: 'We learn about your business, call volume, and specific needs. Discuss use cases, integration requirements, and ROI expectations.',
      duration: '30-45 min'
    },
    {
      step: '2',
      title: 'Custom Configuration',
      description: 'Our team sets up your business profile, hours, staff directory, knowledge base, and call routing logic. You approve everything.',
      duration: '1-2 weeks'
    },
    {
      step: '3',
      title: 'Voice Tuning & Testing',
      description: 'We dial in the AI personality to match your brand. You make test calls, we refine responses until it\'s perfect.',
      duration: '3-5 days'
    },
    {
      step: '4',
      title: 'Go Live & Monitor',
      description: 'Port your number or forward calls. We monitor the first week closely, make quick adjustments, and ensure quality.',
      duration: 'Ongoing'
    }
  ];

  const pricingPlans = [
    {
      name: 'Core Package',
      price: '$149/month',
      headline: 'Perfect for small businesses with straightforward call handling needs.',
      callouts: [
        '100 inbound calls/month (30s max)',
        'Basic knowledge base (10 FAQs)',
        'Business hours + after-hours handling',
        'Call forwarding to 1 owner phone',
        'Email summaries of all calls',
        'Basic analytics dashboard'
      ],
      cta: 'Get Started',
      setup: 'Setup: $299 one-time'
    },
    {
      name: 'Premium Package',
      price: '$449/month',
      headline: 'Growing teams that need routing, automation, and outbound campaigns.',
      callouts: [
        '600 inbound + 100 outbound calls/month',
        'Advanced knowledge base (unlimited)',
        'Multi-department routing logic',
        'Staff directory + availability checking',
        'Appointment capture + calendar sync',
        'Outbound campaigns & reminders',
        'Full call transcription archive',
        'Priority email + phone support'
      ],
      cta: 'Schedule Discovery Call',
      featured: true,
      setup: 'Setup: $599 one-time'
    },
    {
      name: 'Enterprise Package',
      price: '$899/month',
      headline: 'High-volume operations requiring advanced features and priority support.',
      callouts: [
        '1,500 inbound + 300 outbound calls/month',
        'Enterprise routing across locations',
        'CRM & calendar integrations',
        'Advanced survey & data collection',
        'Dedicated success manager',
        'Compliance & quality assurance',
        'Custom API integrations',
        'SLA guarantees + 24/7 support'
      ],
      cta: 'Talk With Sales',
      setup: 'Custom onboarding included'
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleAnchorClick = (e) => {
      const href = e.target.closest('a')?.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    setTimeout(() => {
      document.querySelectorAll('.fade-in').forEach(card => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(30px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observer.observe(card);
      });
    }, 100);

    return () => {
      document.removeEventListener('click', handleAnchorClick);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gray-50 text-gray-900 py-20">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-center md:text-left">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Enterprise-Grade AI Voice<br />With White-Glove Service
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-600">
              We don't just give you software—we configure, tune, and manage your AI voice assistant so it sounds like part of your team.
            </p>
            <p className="text-lg md:text-xl mb-6 font-semibold text-gray-900">
              One AI voice agent that handles the calls across your business — for a fraction of the cost of staffing each role.
            </p>
            <p className="text-lg mb-10 text-gray-500">
              Reception · Customer Service · Sales · HR &amp; Recruiting · Training · Employee Support
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <Mail className="w-5 h-5" />
                Get a Free Demo
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all border border-gray-300"
              >
                See How It Works
              </a>
            </div>
          </div>
          <div className="flex justify-center">
            <img
              src="/images/alfred-character.png"
              alt="Alfred — your AI voice agent, fully set up for you"
              className="rounded-2xl shadow-2xl w-full max-w-md object-cover"
            />
          </div>
        </div>
      </section>

      {/* See Alfred at work */}
      <section className="bg-white text-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <img
            src="/images/alfred-desk.png"
            alt="Alfred handling reception, sales, HR, and support calls 24/7"
            className="rounded-2xl shadow-2xl w-full object-cover"
          />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">One agent. Every call across your business.</h2>
            <p className="text-lg text-gray-600 mb-6">
              Alfred answers, qualifies, books, and routes—so no call goes to voicemail and no role goes unstaffed. You decide what he handles; we set it all up for you.
            </p>
            <ul className="space-y-2 text-lg">
              <li className="flex items-center gap-3"><span className="text-green-600">✓</span> Reception &amp; Support</li>
              <li className="flex items-center gap-3"><span className="text-green-600">✓</span> Sales &amp; Lead Follow-Up</li>
              <li className="flex items-center gap-3"><span className="text-green-600">✓</span> HR &amp; Interviews</li>
              <li className="flex items-center gap-3"><span className="text-green-600">✓</span> Employee Helpdesk</li>
            </ul>
            <p className="mt-6 text-base text-gray-500">Answers every call · 24/7</p>
          </div>
        </div>
      </section>

      {/* Talk to Alfred now — pick a role, get a call */}
      <section id="talk-to-alfred" className="py-20 bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <TalkToAlfred />
        </div>
      </section>

      {/* Why White-Glove Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why We Don't Offer Self-Service</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              AI voice systems are complex. Every business is different. We configure everything for you because one wrong setting can ruin the customer experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="fade-in bg-red-50 border border-red-200 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-red-900 mb-4">❌ Self-Service Problems</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span>Business hours misconfigured → calls routed wrong</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span>Voice personality too formal/casual → brand damage</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span>Knowledge base incomplete → AI gives wrong answers</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span>Call routing broken → customers never reach you</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span>Poor first impression → immediate cancellation</span>
                </li>
              </ul>
            </div>

            <div className="fade-in bg-green-50 border border-green-200 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-green-900 mb-4">✓ Our Approach</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>We configure business hours with your input</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Voice tuned to match your brand personality</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>We build knowledge base from your materials</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Test calls before go-live to verify routing</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 font-bold mt-1">✓</span>
                  <span>Perfect experience from day one</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-100 border border-gray-300 p-8 rounded-2xl fade-in">
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong className="text-black">This is a services business, not self-service software.</strong> Every configuration we do is based on a discovery call where we learn your business. We set it up right, test thoroughly, and monitor closely during launch. You get a dedicated success manager, not a signup form.
            </p>
          </div>
        </div>
      </section>

      {/* Technical Capabilities Section */}
      <section id="capabilities" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Enterprise Features, Fully Configured</h2>
            <p className="text-xl text-gray-600">
              All of these are included in your plan—we set them up during onboarding
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technicalFeatures.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="fade-in bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center mb-6 text-white">
                  <Icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">{title}</h3>
                <p className="text-gray-600 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inbound vs Outbound Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Inbound + Outbound Call Handling</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              One platform handles both directions. Different AI logic for each use case.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Inbound */}
            <div className="fade-in bg-gray-50 border-2 border-gray-300 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center text-white">
                  <PhoneIncoming className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold text-black">Inbound Calls</h3>
              </div>
              <p className="text-gray-700 mb-6">
                AI answers on the first ring, checks business hours, routes to the right department, and takes messages when you're unavailable.
              </p>
              <ul className="space-y-3 text-gray-700">
                {inboundFeatures.map(feature => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-800 font-semibold">
                  ✓ Multiple concurrent calls<br />
                  ✓ Never miss an opportunity<br />
                  ✓ Instant email notifications
                </p>
              </div>
            </div>

            {/* Outbound */}
            <div className="fade-in bg-gray-50 border-2 border-gray-300 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gray-800 rounded-xl flex items-center justify-center text-white">
                  <PhoneOutgoing className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold text-black">Outbound Campaigns</h3>
              </div>
              <p className="text-gray-700 mb-6">
                AI follows structured scripts, asks specific questions, records responses, and knows when to leave messages vs. retry later.
              </p>
              <ul className="space-y-3 text-gray-700">
                {outboundFeatures.map(feature => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-800 font-semibold">
                  ✓ Scheduled campaigns<br />
                  ✓ Script compliance tracking<br />
                  ✓ Voicemail detection (doesn't engage)
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gray-100 p-8 rounded-2xl fade-in">
            <h4 className="text-2xl font-bold mb-4 text-center">Different AI Logic for Each Direction</h4>
            <div className="grid md:grid-cols-2 gap-6 text-gray-700">
              <div>
                <p className="font-semibold text-black mb-2">Inbound AI Behavior:</p>
                <p>Open-ended, conversational. Handles objections, answers questions, adapts to customer needs. Can transfer to humans.</p>
              </div>
              <div>
                <p className="font-semibold text-black mb-2">Outbound AI Behavior:</p>
                <p>Script-focused, structured. Delivers message, asks specific questions, records answers. Ends call after objectives met.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Capabilities Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Internal AI Capabilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Beyond customer service—AI that drives employee communications and acts as your technical expert
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Employee Communications */}
            <div className="fade-in bg-gray-50 border-2 border-gray-300 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center text-white">
                  <Users className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold text-black">Employee Communications</h3>
              </div>
              <p className="text-gray-700 mb-6">
                Automate internal communications that would otherwise be time-consuming tasks. The AI calls your staff with updates, verifies schedules, and delivers important information.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                  <span>Schedule updates and shift change notifications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                  <span>Company news and announcement delivery</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                  <span>Shift verification and attendance confirmation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                  <span>Emergency notifications to staff members</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                  <span>Policy updates and compliance reminders</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                  <span>General internal communications automation</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-800 font-semibold">
                  ✓ Saves hours of manual calling<br />
                  ✓ Ensures consistent messaging<br />
                  ✓ Tracks confirmation and responses
                </p>
              </div>
            </div>

            {/* Technical Expert */}
            <div className="fade-in bg-gray-50 border-2 border-gray-300 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gray-800 rounded-xl flex items-center justify-center text-white">
                  <Brain className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold text-black">Technical Expert</h3>
              </div>
              <p className="text-gray-700 mb-6">
                The AI can act as a technical consultant in meetings and presentations, explaining complex specifications in understandable terms without anyone needing to read manuals.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                  <span>Join meetings as technical expert on call</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                  <span>Explain product specifications in simple terms</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                  <span>Answer technical questions during presentations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                  <span>Bridge gap between technical and non-technical teams</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                  <span>Provide instant access to product knowledge</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-800 mt-2"></span>
                  <span>Support sales presentations with technical details</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-800 font-semibold">
                  ✓ No manual reading required<br />
                  ✓ Always available expert knowledge<br />
                  ✓ Consistent technical accuracy
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gray-100 p-8 rounded-2xl fade-in">
            <h4 className="text-2xl font-bold mb-4 text-center">Three Complete AI Capabilities</h4>
            <div className="grid md:grid-cols-3 gap-6 text-gray-700">
              <div>
                <p className="font-semibold text-black mb-2">External - Customer Service:</p>
                <p>Handle inbound customer calls, make outbound campaigns, answer questions, route calls, and never miss an opportunity.</p>
              </div>
              <div>
                <p className="font-semibold text-black mb-2">Internal - Employee Communications:</p>
                <p>Automate time-consuming internal communications tasks like schedule updates, news delivery, and shift confirmations.</p>
              </div>
              <div>
                <p className="font-semibold text-black mb-2">Internal - Technical Expert:</p>
                <p>Provide instant technical expertise in meetings and presentations without requiring staff to memorize product specifications.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works / White-Glove Process */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our White-Glove Onboarding Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From first call to go-live in 2-3 weeks. We do the heavy lifting.
            </p>
          </div>

          <div className="space-y-8">
            {whiteGloveProcess.map(({ step, title, description, duration }) => (
              <div key={step} className="fade-in flex gap-6 bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex-shrink-0 w-16 h-16 bg-black rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                  {step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-2xl font-bold">{title}</h3>
                    <span className="text-sm font-semibold text-black bg-gray-100 px-4 py-2 rounded-full">
                      {duration}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gray-100 border border-gray-300 p-8 rounded-2xl fade-in text-center">
            <p className="text-lg text-gray-700">
              <strong className="text-black">After go-live:</strong> We monitor your first week closely, make quick adjustments, and stay available for any questions. You get a dedicated success manager—not a support ticket system.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">
              All plans include white-glove setup, configuration, tuning, and ongoing support
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map(plan => {
              const cardClasses = plan.featured
                ? 'fade-in bg-gray-50 p-8 rounded-3xl border-2 border-black shadow-xl hover:-translate-y-2 transition-transform'
                : 'fade-in bg-white p-8 rounded-3xl border border-gray-200 shadow-md hover:-translate-y-2 transition-transform';

              return (
                <div key={plan.name} className={cardClasses}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold uppercase tracking-wider text-black">
                      {plan.name}
                    </div>
                    {plan.featured && (
                      <span className="text-xs font-semibold text-white bg-black px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <div className="text-4xl font-extrabold text-gray-900 mb-2">{plan.price}</div>
                  <div className="text-sm text-gray-500 mb-4">{plan.setup}</div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{plan.headline}</p>
                  <ul className="space-y-3 text-gray-700 mb-8">
                    {plan.callouts.map(item => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="inline-block w-2 h-2 rounded-full bg-black mt-2"></span>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handlePlanSelect(plan.name)}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                      plan.featured
                        ? 'bg-black text-white hover:bg-gray-800 hover:shadow-xl'
                        : 'bg-gray-800 text-white hover:bg-black'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center fade-in">
            <p className="text-gray-600 mb-6">
              <strong>Need more calls?</strong> Additional usage available at $0.25/min for inbound, $0.30/min for outbound.<br />
              Enterprise volume discounts available.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-black font-semibold hover:text-gray-700"
            >
              Questions about pricing? Contact us →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-100 text-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Phone System?
          </h2>
          <p className="text-xl mb-10 text-gray-600">
            Schedule a discovery call and we'll show you exactly how Alfred Voice will work for your business.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-10 py-5 rounded-xl font-bold text-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
          >
            <Mail className="w-6 h-6" />
            Schedule Discovery Call
          </Link>
          <p className="mt-6 text-sm text-gray-500">
            No pressure, no commitment. Just a conversation about your needs.
          </p>
        </div>
      </section>

      <Footer />

      {/* Subscription Interest Form Modal */}
      {isFormOpen && (
        <SubscriptionInterestForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedPlan(null);
          }}
          selectedPlan={selectedPlan}
        />
      )}
    </div>
  );
};

export default LandingPageEnhanced;
